/**
 * Dose Resolver
 * Resolves which dose band applies to a patient
 * Pure function, no side effects
 */

import type { PGD, DoseBand, PatientInput } from "../types";

/**
 * Find the dose band that applies to this patient
 * Returns null if no band matches
 */
export function resolveDoseBand(pgd: PGD, patient: PatientInput, doseStepId?: string): DoseBand | null {
  if (doseStepId) {
    return pgd.doseBands.find(b => b.doseStepId === doseStepId) ?? pgd.doseBands[0];
  }

  for (const band of pgd.doseBands) {
    if (!bandMatches(band, patient)) continue;

    return band;
  }

  return null;
}

/**
 * Check if a dose band matches the patient
 */
function bandMatches(band: DoseBand, patient: PatientInput): boolean {
  if (band.resolveBy === "single") {
    return true;
  }

  if (band.resolveBy === "age" && band.ageRange) {
    if (
      patient.ageYears < band.ageRange.min ||
      patient.ageYears > band.ageRange.max
    ) {
      return false;
    }
    return true;
  }

  if (band.resolveBy === "weight" && band.weightRange) {
    if (!patient.weightKg) return false;
    if (
      patient.weightKg < band.weightRange.minKg ||
      patient.weightKg > band.weightRange.maxKg
    ) {
      return false;
    }
    return true;
  }

  return false;
}

/**
 * Calculate the actual dose for a band
 * Currently supports only constant doses
 * TODO: mg/kg formulas when needed
 */
export function calculateDose(
  band: DoseBand,
  patient: PatientInput,
): number | string {
  if (band.mgPerKg && typeof patient.weightKg === "number") {
    return Math.round(patient.weightKg * band.mgPerKg * 10) / 10;
  }
  return band.dose;
}

/**
 * Render a dosing instruction from a dose band + formulation
 */
export function renderInstruction(instruction: string): string {
  return instruction;
}

/**
 * Validate that required weight input is available if needed
 */
export function requiresWeightInput(pgd: PGD, patient: PatientInput): boolean {
  if (!pgd.requiresWeightInput) return false;
  if (patient.ageYears < pgd.requiresWeightInput.min) return true;
  if (patient.ageYears > pgd.requiresWeightInput.max) return true;
  return !patient.weightKg;
}
